<template>
    <div 
        class="flow-item" 
        :class="{ 
            'flow-item-lighter': lighter,
            'flow-item-darker': !lighter,
            'flow-item-highlight': highlight
        }">

        <div class="flow-item-details">

            <div class="flow-item-details-title">
                <span class="prompt">{{ prompt }}</span>
            </div>

            <div class="flow-item-details-options">
                <v-btn
                    class="flow-item-button-warning"
                    variant="tonal" 
                    elevation="0"
                    @click.stop="handleDeleteFlow">
                    <v-icon icon="fa-solid fa-trash"></v-icon>
                    <v-tooltip
                        activator="parent"
                        location="bottom">
                        delete flow and all its assets
                    </v-tooltip>
                </v-btn>
            </div>

        </div>

        <div class="flow-item-content">

            <FlowItemStack 
                v-if="hasOutputAssets"
                type="model" 
                :justify="count <= 1 ? 'center' : 'left'" 
                :assets="outputAssets" 
                :lighter="lighter"
                :limit="limit"
                @select="handleSelect" 
                @rate="handleRate" 
                @share="handleShare" 
                @download="handleDownload"
                @delete="handleDelete" />

            <FlowItemDivider 
                v-if="hasInputAssets && hasOutputAssets"
                class="flow-item-content-divider"
                :lighter="lighter" />

            <FlowItemStack 
                v-if="hasInputAssets"
                type="image"
                justify="center" 
                :assets="inputAssets" 
                :lighter="lighter"
                :limit="limit"
                @select="handleSelect" 
                @rate="handleRate" 
                @share="handleShare" 
                @download="handleDownload"
                @delete="handleDelete" />

        </div>

    </div>
</template>

<script>
import APPS from '@generio-ai/library-legacy/constants/apps'
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import DIALOGS from '@generio-ai/library-legacy/constants/dialogs'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import FlowItemStack from '@/components/library/flow/FlowItemStack.vue'
import FlowItemDivider from '@/components/library/flow/FlowItemDivider.vue'

    export default {
        name: 'FlowItem',
        components: {
            FlowItemStack,
            FlowItemDivider
        },
        props: {
            item: {
                type: Object,
                required: true
            },
            lighter: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                highlight: false
            }
        },
        computed: {
            ...mapGetters('flows', [
                'isInputAsset',
                'isOutputAsset'
            ]),

            keyAssetTypeImage() {
                return ASSETS.TYPES.IMAGE
            },
            keyAssetTypeModel() {
                return ASSETS.TYPES.MODEL
            },

            keyAppCreate() {
                return APPS.CREATE
            },
            keyAppModify() {
                return APPS.MODIFY
            },
            keyAppSketch2() {
                return APPS.SKETCH2
            },
            keyAppSketch() {
                return APPS.SKETCH
            },

            hasInputAssets() {
                return this.inputAssets.length > 0
            },
            hasOutputAssets() {
                return this.outputAssets.length > 0
            },

            flow() {
                return this.item?.flow || 0
            },
            assets() {
                return this.item?.assets || []
            },

            count() {
                return [
                    this.hasInputAssets, this.hasOutputAssets
                ].filter(Boolean).length
            },
            limit() {
                switch (this.count) {
                    case 3: return 2
                    case 2: return 3
                    case 1: return 6
                    default: return 0
                }
            },

            inputAssets() {
                return this.assets?.filter(asset => this.isInputAsset(asset))
            },
            outputAssets() {
                return this.assets?.filter(asset => this.isOutputAsset(asset))
            },
            allAssets() {
                return [...this.inputAssets, ...this.outputAssets]
            },

            lastAsset() {
                if (this.allAssets.length === 0) return null
                return this.allAssets?.reduce((newest, asset) => 
                    asset.update > newest.update ? asset : newest)
            },

            prompt() {
                if (this.lastAsset?.app === this.keyAppSketch) {
                    return this.lastAsset?.additional?.positive || ''
                } else if (this.lastAsset?.app === this.keyAppSketch2) {
                    return this.lastAsset?.additional?.prompt?.user || 
                        this.lastAsset?.additional?.prompt?.positive || ''
                }
                return ''
            }
        },
        emits: ['select', 'rate', 'share', 'download', 'delete'],
        methods: {
            ...mapActions('dialogs', [
                'open'
            ]),
            ...mapActions('library', [
                'deleteAssets'
            ]),
            ...mapActions('notifications', [
                'show'
            ]),
            handleSelect(asset) {
                this.$emit('select', asset)
            },
            handleRate(asset) {
                this.$emit('rate', asset)
            },
            handleShare(asset) {
                this.$emit('share', asset)
            },
            handleDownload(asset) {
                this.$emit('download', asset)
            },
            handleDelete(asset) {
                this.$emit('delete', asset)
            },
            handleDeleteFlow() {
                this.highlight = true
                this.open({
                    component: DIALOGS.CHOICE,
                    payload: {
                        title: 'Delete&nbsp;Flow',
                        message: 'Are you sure you want to delete the flow and all its assets?',
                        firstLabel: 'Cancel',
                        firstColor: 'surface',
                        onFirst: () => {
                            this.highlight = false
                        },
                        secondLabel: 'Delete',
                        secondColor: 'error',
                        onSecond: () => {
                            this.highlight = false
                            this.deleteAssets(this.assets)
                            .then(() => {
                                this.show({
                                    message: 'Flow and all its assets deleted successfully.',
                                    level: LEVELS.INFORMATION,
                                })
                            })
                            .catch(error => {
                                this.show({
                                    message: 'Failed to delete flow and its assets.',
                                    level: LEVELS.ERROR
                                })
                            })
                        }
                    }
                })
            }
        }
    }
</script>

<style scoped>
    .flow-item {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 10px;
        background: linear-gradient(to bottom, transparent, rgba(var(--v-theme-darkerbase), 0.3));
    }

    .flow-item:hover {
        /*background: rgba(var(--v-theme-color1), 0.4);*/
        background: linear-gradient(to bottom, rgba(var(--v-theme-color1), 0.1), rgba(var(--v-theme-color1), 0.3));
    }

    .flow-item .prompt {
        display: none;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .flow-item:hover .prompt {
        display: block;
    }

    .flow-item-lighter {
        /*background: rgba(var(--v-theme-background), 0.8);*/
    }

    .flow-item-darker {
        /*background: linear-gradient(to bottom, rgb(var(--v-theme-lightbase)), rgb(var(--v-theme-darkbase)));*/
    }

    .flow-item-highlight {
        background: linear-gradient(to bottom, transparent, rgba(var(--v-theme-color1), 0.4));
        /*background: rgba(var(--v-theme-color1), 0.4);*/
    }

    .flow-item-content {
        width: 100%;
        flex-grow: 1;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 10px;
    }

    .flow-item-details {
        min-height: 36px;
        width: 100%;
        flex-shrink: 1;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
    }

    .flow-item-details-title {
        flex-grow: 1;
        font-size: 1.3rem;
        margin-left: 75px;
        text-transform: capitalize;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
    }

    .flow-item-details-options {
        box-sizing: border-box;
        flex-shrink: 1;
        min-width: 75px;
        padding-right: 10px;
    }

    .flow-item-button-color:hover {
        background-color: rgb(var(--v-theme-color1));
    }

    .flow-item-button-warning {
        display: none;
    }

    .flow-item-button-warning:hover {
        background-color: rgb(var(--v-theme-error));
    }

    .flow-item:hover .flow-item-button-warning {
        display: flex;
    }

    @media (max-width: 2000px) {
        .flow-item-details-title {
            font-size: 20px;
        }
    }   

    @media (max-width: 1700px) {
        .flow-item-details-title {
            font-size: 18px;
        }
    }  

    @media (max-width: 1400px) {
        .flow-item-details-title {
            font-size: 16px;
        }
    }

    @media (max-width: 1000px) {
        .flow-item-details-title {
            font-size: 15px;
        }
    }

    @media (max-width: 800px) {
        .flow-item-details-title {
            margin-left: 10px;
            display: flex;
            justify-content: flex-start;
        }

        .flow-item-content {
            flex-direction: column;
        }

        .flow-item-content-divider {
            display: none;
        }
    }
</style>